import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { type PeriodStats } from '@apm/contracts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { errorMessage } from '../../../lib/error-message';
import { reportApi, reportKeys } from '../api';

/**
 * Kỳ này so với kỳ trước — APM-MAINT-f.
 *
 * ## Vì sao dự án bảo trì cần đúng bảng này
 *
 * Nó không có đường găng, baseline hay ngày kết thúc, nên mọi biểu đồ kiểu "còn
 * bao xa nữa" đều vô nghĩa. Câu hỏi thật của người vận hành là **có kham nổi
 * không** — và câu ấy chỉ trả lời được bằng cách so kỳ này với kỳ trước.
 *
 * ## Vì sao bảng chứ không phải biểu đồ
 *
 * Chỉ có hai cột và năm hàng. Một biểu đồ cột cho mười con số ấy sẽ dựng cả một
 * trục toạ độ để nói điều mà hai cột số nói ngắn hơn — và mấy chỉ số này có đơn
 * vị khác nhau (việc, phần trăm, ngày) nên chúng không dùng chung được trục
 * nào.
 *
 * ## Vì sao "tăng" không phải lúc nào cũng tốt
 *
 * Năm chỉ số có hai chiều tốt khác nhau: xong nhiều việc hơn là tốt, còn tồn
 * đọng nhiều hơn thì không. Nên mũi tên chỉ nói **hướng**, còn màu nói **lành
 * hay dữ** — hai kênh cho hai câu, xem `METRICS`.
 */
interface Metric {
  readonly key: keyof PeriodStats;
  /** Tăng lên là tin tốt hay tin xấu. `null` = không phán xét, chỉ hiện số. */
  readonly higherIsBetter: boolean | null;
  readonly suffix?: string;
}

const METRICS: readonly Metric[] = [
  { key: 'created', higherIsBetter: null },
  { key: 'completed', higherIsBetter: true },
  { key: 'openAtEnd', higherIsBetter: false },
  { key: 'unplannedPct', higherIsBetter: false, suffix: '%' },
  { key: 'avgCycleDays', higherIsBetter: false },
];

export function PeriodComparison({
  projectId,
  period,
}: {
  readonly projectId: string;
  readonly period: string;
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: reportKeys.periodComparison(projectId, period),
    queryFn: () => reportApi.periodComparison(projectId, period),
  });

  return (
    <ChartFrame
      title={t('reports.periodComparisonTitle')}
      help="periodComparison"
      {...(report.data
        ? {
            subtitle: t('reports.periodComparisonSubtitle', {
              previous: report.data.previousPeriod,
            }),
          }
        : {})}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
    >
      {report.data ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th scope="col" className="py-1 text-left font-medium">
                {t('reports.periodMetric')}
              </th>
              <th scope="col" className="py-1 text-right font-medium">
                {t('reports.periodCurrent')}
              </th>
              <th scope="col" className="py-1 text-right font-medium">
                {t('reports.periodPrevious')}
              </th>
              <th scope="col" className="py-1 text-right font-medium">
                {t('reports.periodDelta')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {METRICS.map((metric) => (
              <Row
                key={metric.key}
                metric={metric}
                current={report.data.current[metric.key]}
                previous={report.data.previous[metric.key]}
              />
            ))}
          </tbody>
        </table>
      ) : null}
    </ChartFrame>
  );
}

function Row({
  metric,
  current,
  previous,
}: {
  readonly metric: Metric;
  readonly current: number | null;
  readonly previous: number | null;
}): ReactNode {
  const { t } = useTranslation();

  /*
    Thiếu một trong hai đầu thì **không** dựng chênh lệch.

    `null` ở đây nghĩa là "chưa đủ dữ liệu để nói" (xem `PeriodStats`), nên coi
    nó là 0 sẽ biến một kỳ chưa có số liệu thành một kỳ giảm mạnh — đúng loại
    con số người ta mang đi họp.
  */
  const delta = current !== null && previous !== null ? current - previous : null;
  const tone =
    delta === null || delta === 0 || metric.higherIsBetter === null
      ? 'text-muted-foreground'
      : delta > 0 === metric.higherIsBetter
        ? 'text-rag-green'
        : 'text-rag-red';

  const Arrow = delta === null || delta === 0 ? Minus : delta > 0 ? ArrowUp : ArrowDown;

  return (
    <tr>
      <th scope="row" className="py-1.5 text-left font-normal">
        {t(`reports.periodMetricName.${metric.key}`)}
      </th>
      <td className="py-1.5 text-right font-medium tabular-nums">{show(current, metric.suffix)}</td>
      <td className="py-1.5 text-right tabular-nums text-muted-foreground">
        {show(previous, metric.suffix)}
      </td>
      <td className={`py-1.5 text-right tabular-nums ${tone}`}>
        <span className="inline-flex items-center gap-0.5">
          <Arrow aria-hidden className="size-3.5" />
          {delta === null ? '—' : show(Math.abs(delta), metric.suffix)}
        </span>
      </td>
    </tr>
  );
}

/** `null` thành "—": một ô trống đọc như dữ liệu chưa tải xong. */
function show(value: number | null, suffix?: string): string {
  return value === null ? '—' : `${value}${suffix ?? ''}`;
}
